import React, { useEffect, useRef, useState } from 'react';

const STRIPE = "https://stan.store/mohammedalubaidy/p/effortless-rizq";

// ── Placeholder component shown until real images are dropped in ──
const Img = ({ slot, alt, style = {}, className = "" }) => (
  <div className={`img-placeholder ${className}`} style={style} title={`Image slot: ${slot}`}>
    <div className="img-placeholder-inner">
      <span className="img-placeholder-icon">🖼</span>
      <span className="img-placeholder-label">{slot}</span>
      <span className="img-placeholder-alt">{alt}</span>
    </div>
  </div>
);

const Divider = () => (
  <div className="islamic-divider">
    <div className="divider-line" />
    <div className="divider-motif">
      <span className="div-wing">❧</span>
      <span className="div-diamond">◈</span>
      <span className="div-star">✦</span>
      <span className="div-diamond">◈</span>
      <span className="div-wing flip">❧</span>
    </div>
    <div className="divider-line" />
  </div>
);

const Stars = ({ text = "Over 1,000 Muslims Enrolled" }) => (
  <div className="stars-row">
    <span className="stars">⭐⭐⭐⭐⭐</span>
    <span className="stars-text">{text}</span>
  </div>
);

const OrderBtn = ({ label = "YES — I'm Ready to Realign →", price = true }) => (
  <div className="cta-block">
    <a href={STRIPE} className="cta-btn">
      {label}{price ? " $299" : ""}
    </a>
    <p className="cta-sub">Secure Checkout · Instant Access · 60-Day Guarantee</p>
    <Stars />
  </div>
);

const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Tajawal:wght@300;400;500;700;800&family=Lato:ital,wght@0,300;0,400;1,300&display=swap');

:root {
  --gold: #C9A84C;
  --gold-lt: #E8CC7A;
  --gold-pale: #F5E9C8;
  --dark: #0F1F18;
  --dark2: #0B1812;
  --mid-dark: #162B22;
  --white: #FAF8F4;
  --cream: #F5F0E8;
  --cream2: #EDE8DC;
  --pale-green: #F0F5F2;
  --pale-amber: #FBF6EC;
  --warm-gray: #F4F2EE;
  --text-dark: #1A1614;
  --text-body: #3A3530;
  --text-muted: #7A7268;
  --emerald: #2D6A4F;
  --emerald-lt: #52B788;
}

*, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
html { scroll-behavior:smooth; }
body {
  background: var(--white);
  color: var(--text-body);
  font-family: 'Lato', sans-serif;
  font-weight: 300;
  line-height: 1.8;
  overflow-x: hidden;
}

/* ── PROGRESS BAR ── */
#prog {
  position:fixed; top:0; left:0; height:3px;
  background: linear-gradient(90deg, var(--gold), var(--gold-lt), var(--gold));
  z-index:9999; width:0%; transition:width .1s;
}

/* ── STICKY BAR ── */
.sbar {
  position:fixed; bottom:0; left:0; right:0;
  background: rgba(11,24,18,0.97);
  border-top: 1px solid rgba(201,168,76,0.3);
  backdrop-filter: blur(10px);
  padding: 12px 24px;
  display:flex; align-items:center; justify-content:space-between;
  z-index:998; transform:translateY(100%); transition:transform 0.4s ease;
}
.sbar.on { transform:translateY(0); }
.sbar-txt { font-size:13px; color:rgba(232,204,122,0.7); }
.sbar-txt strong { color:var(--gold-lt); }
.sbar-btn {
  background: var(--gold); color: var(--dark2);
  font-family:'Tajawal',sans-serif; font-weight:700;
  font-size:12px; letter-spacing:.1em; text-transform:uppercase;
  padding:10px 24px; text-decoration:none; white-space:nowrap;
}

/* ── ANNOUNCE ── */
.announce {
  background: var(--dark2);
  border-bottom: 1px solid rgba(201,168,76,0.2);
  text-align:center; padding:10px 16px;
  font-family:'Tajawal',sans-serif; font-size:12px; font-weight:500;
  color:var(--gold-lt); letter-spacing:.12em; text-transform:uppercase;
}

/* ══════════════════════════════════════════
   SECTION 1 — HERO (DARK GREEN)
══════════════════════════════════════════ */
.s-hero {
  background: linear-gradient(170deg, var(--dark2) 0%, var(--dark) 50%, var(--mid-dark) 100%);
  position:relative; overflow:hidden;
}
.s-hero::before {
  content:'رزق';
  position:absolute; top:50%; left:50%;
  transform:translate(-50%,-50%);
  font-family:'Tajawal',sans-serif; font-size:55vw;
  color:rgba(201,168,76,0.025);
  pointer-events:none; z-index:0; white-space:nowrap; user-select:none;
}
.hero-inner {
  position:relative; z-index:1;
  max-width:860px; margin:0 auto;
  padding:56px 24px 72px; text-align:center;
}
.hero-logo-slot { margin-bottom:36px; }

/* HERO ATMO IMAGE SLOT */
.hero-atmo-slot {
  width:100%; max-height:340px; overflow:hidden;
  position:relative; margin-bottom:0;
}
.hero-atmo-slot .img-placeholder {
  height:340px; background:linear-gradient(135deg,#0B1812,#1a2e22);
  display:flex; align-items:center; justify-content:center;
}

.hero-headline {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(26px,4.8vw,52px); font-weight:400;
  line-height:1.15; color:var(--white);
  padding:28px 24px 28px; max-width:860px; margin:0 auto;
  text-align:center;
}
.hero-headline em { font-style:italic; color:var(--gold-lt); }

.hero-subhead {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(16px,2vw,20px); color:rgba(232,204,122,0.75);
  max-width:720px; margin:0 auto 40px;
  border:1px solid rgba(201,168,76,0.2);
  padding:18px 28px;
  background:rgba(201,168,76,0.05);
  text-align:center;
}

.sound-note {
  font-size:12px; letter-spacing:.08em; color:var(--gold-lt);
  margin-bottom:20px; display:flex; align-items:center; justify-content:center; gap:8px;
}
.vwrap {
  max-width:740px; margin:0 auto 36px;
  box-shadow:0 0 60px rgba(0,0,0,0.5);
  border:1px solid rgba(201,168,76,0.25);
}
.vwrap iframe { display:block; width:100%; aspect-ratio:16/9; border:none; }

/* TRUST BAR */
.trust-bar {
  display:flex; align-items:center; justify-content:center;
  gap:clamp(16px,4vw,40px); flex-wrap:wrap;
  padding:20px 24px;
  border-top:1px solid rgba(201,168,76,0.12);
  margin-top:20px;
}
.trust-item {
  display:flex; align-items:center; gap:8px;
  font-size:12px; color:rgba(201,168,76,0.55);
  letter-spacing:.06em; text-transform:uppercase;
}
.trust-item .trust-icon { font-size:18px; opacity:.7; }

/* ══════════════════════════════════════════
   SHARED LAYOUT
══════════════════════════════════════════ */
.col { max-width:700px; margin:0 auto; padding:0 24px; }
.col-wide { max-width:920px; margin:0 auto; padding:0 24px; }

/* ══════════════════════════════════════════
   SECTION BACKGROUNDS (alternating)
══════════════════════════════════════════ */
.s-white   { background:var(--white); }
.s-cream   { background:var(--cream); }
.s-cream2  { background:var(--cream2); }
.s-pale-green { background:var(--pale-green); }
.s-pale-amber { background:var(--pale-amber); }
.s-warm-gray { background:var(--warm-gray); }
.s-dark    { background:linear-gradient(170deg,var(--dark2),var(--dark),var(--mid-dark)); }
.s-off-white { background:#F7F4EE; }

.sec-pad { padding:80px 0; }
.sec-pad-lg { padding:100px 0; }
.sec-pad-sm { padding:60px 0; }

/* ══════════════════════════════════════════
   TYPOGRAPHY
══════════════════════════════════════════ */
p {
  margin-bottom:22px; font-size:clamp(16px,1.9vw,18px);
  color:var(--text-body); font-weight:300; line-height:1.85;
}
p:last-child { margin-bottom:0; }
strong { color:var(--text-dark); font-weight:600; }

.sec-label {
  font-family:'Tajawal',sans-serif; font-size:10px;
  letter-spacing:.35em; text-transform:uppercase; color:var(--gold);
  display:block; margin-bottom:14px;
}
.sec-h2 {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(28px,4.8vw,48px); font-weight:400;
  color:var(--text-dark); line-height:1.15; margin-bottom:28px;
}
.sec-h2 em { font-style:italic; color:var(--emerald); }
.sec-h2-dark { color:var(--white); }
.sec-h2-dark em { color:var(--gold-lt); }
.sec-h2-center { text-align:center; }
.sec-h3 {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(20px,3vw,28px); font-weight:400;
  color:var(--text-dark); line-height:1.25; margin-bottom:14px;
}

/* ARABIC */
.arabic-display {
  font-family:'Tajawal',sans-serif;
  font-size:clamp(26px,4vw,42px); color:var(--gold);
  direction:rtl; text-align:center;
  margin-bottom:10px; opacity:.9; line-height:1.7;
}
.arabic-trans {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(13px,1.8vw,16px); color:var(--text-muted);
  text-align:center; margin-bottom:0;
}

/* PULL QUOTE */
.pq {
  border-left:3px solid var(--gold);
  padding:22px 32px; margin:36px 0;
  background:rgba(201,168,76,0.05);
}
.pq p {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(20px,2.8vw,26px); color:var(--text-dark);
  margin-bottom:8px; font-weight:300; line-height:1.5;
}
.pq cite { font-size:12px; color:var(--gold); letter-spacing:.1em; text-transform:uppercase; font-style:normal; }
.pq-dark { background:rgba(201,168,76,0.06); border-left-color:var(--gold-lt); }
.pq-dark p { color:var(--white); }
.pq-dark cite { color:var(--gold-lt); }

/* ORNAMENT DIVIDER */
.ornament {
  display:flex; align-items:center; justify-content:center;
  gap:0; max-width:480px; margin:56px auto; padding:0 24px;
}
.orn-line {
  flex:1; height:1px;
  background:linear-gradient(90deg,transparent,rgba(201,168,76,0.4),rgba(201,168,76,0.15));
}
.orn-line:last-child {
  background:linear-gradient(90deg,rgba(201,168,76,0.15),rgba(201,168,76,0.4),transparent);
}
.orn-star {
  color:var(--gold); font-size:11px; opacity:.75;
  padding:0 10px; display:flex; align-items:center; gap:6px;
  white-space:nowrap;
}

/* ISLAMIC DIVIDER (for testimonials) */
.islamic-divider {
  display:flex; align-items:center; gap:0;
  max-width:380px; margin:28px auto;
}
.divider-line {
  flex:1; height:1px;
  background:linear-gradient(90deg,transparent,rgba(201,168,76,0.5),rgba(201,168,76,0.2));
}
.divider-line:last-child {
  background:linear-gradient(90deg,rgba(201,168,76,0.2),rgba(201,168,76,0.5),transparent);
}
.divider-motif {
  display:flex; gap:3px; align-items:center;
  padding:0 10px;
}
.div-wing { font-size:16px; color:rgba(201,168,76,0.65); line-height:1; }
.div-wing.flip { transform:scaleX(-1); display:inline-block; }
.div-diamond { font-size:10px; color:rgba(201,168,76,0.5); }
.div-star { font-size:12px; color:var(--gold); opacity:0.8; }

/* AYAH BOX */
.ayah-box {
  border-top:1px solid rgba(201,168,76,0.25);
  border-bottom:1px solid rgba(201,168,76,0.25);
  padding:28px 24px; margin:28px 0; text-align:center;
  background:rgba(201,168,76,0.04);
}
.ayah-ar { font-family:'Tajawal',sans-serif; font-size:clamp(20px,3vw,30px); color:#6B8F71; direction:rtl; line-height:1.9; margin-bottom:12px; }
.ayah-en { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:clamp(16px,2.2vw,20px); color:var(--text-muted); }
.ayah-src { font-size:10px; color:var(--gold); letter-spacing:.2em; text-transform:uppercase; margin-top:10px; }

/* OPEN LOOP HINT */
.open-loop {
  background:rgba(45,106,79,0.06); border-left:2px solid var(--emerald);
  padding:14px 20px; margin:24px 0;
  font-style:italic; color:var(--emerald); font-size:15px;
}

/* ══════════════════════════════════════════
   IMAGE PLACEHOLDERS
══════════════════════════════════════════ */
.img-placeholder {
  background: linear-gradient(135deg, #E8E4DC, #D8D4CC);
  border: 2px dashed rgba(201,168,76,0.35);
  display:flex; align-items:center; justify-content:center;
  width:100%; position:relative;
}
/* Shared border style for all real images */
.themed-img {
  display:block;
  border: 1px solid rgba(201,168,76,0.28);
  box-shadow: 0 6px 32px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.04);
}
.img-placeholder-inner {
  display:flex; flex-direction:column; align-items:center;
  gap:6px; padding:24px; text-align:center;
}
.img-placeholder-icon { font-size:32px; opacity:.5; }
.img-placeholder-label {
  font-family:'Tajawal',sans-serif; font-size:11px;
  font-weight:700; letter-spacing:.2em; text-transform:uppercase;
  color:var(--gold); opacity:.7;
}
.img-placeholder-alt { font-size:12px; color:var(--text-muted); max-width:240px; line-height:1.4; }

/* Specific image slot sizes */
.img-slot-hero-atmo  { min-height:320px; }
.img-slot-logo       { height:60px; max-width:200px; margin:0 auto; background:transparent; border-style:dashed; }
.img-slot-trust      { height:56px; background:transparent; border-style:dashed; border-color:rgba(201,168,76,0.2); }
.img-slot-story      { min-height:300px; max-width:580px; margin:36px auto; }
.img-slot-problem    { min-height:260px; max-width:560px; margin:36px auto; }
.img-slot-fitrah     { min-height:220px; max-width:600px; margin:36px auto; background:linear-gradient(135deg,#E8F5EE,#F5F0E0); }
.img-slot-quran      { min-height:300px; max-width:100%; }
.img-slot-author     { min-height:280px; max-width:440px; margin:36px auto; }
.img-slot-product    { min-height:360px; max-width:640px; margin:40px auto; }
.img-slot-testi      { height:56px; width:56px; min-height:unset; border-radius:50%; background:linear-gradient(135deg,#D8D4CC,#E8E4DC); }
.img-slot-guarantee  { height:130px; width:130px; min-height:unset; border-radius:50%; margin:0 auto 24px; background:linear-gradient(135deg,#E8F5EE,#D8F0E4); }
.img-slot-final      { min-height:280px; max-width:100%; }
.img-slot-icon-sm    { height:40px; width:40px; min-height:unset; border-radius:4px; background:rgba(45,106,79,0.1); border-color:rgba(45,106,79,0.2); flex-shrink:0; }

/* ══════════════════════════════════════════
   SECTION 2 — STORY HOOK (WHITE)
══════════════════════════════════════════ */
.story-open {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(20px,3.5vw,32px); font-weight:300;
  line-height:1.5; color:var(--text-dark);
  margin-bottom:28px;
}

/* ══════════════════════════════════════════
   SECTION 4 — BELIEFS BOX
══════════════════════════════════════════ */
.beliefs-box {
  background:var(--pale-amber); border:1px solid rgba(201,168,76,0.2);
  padding:32px 36px; margin:36px 0;
}
.beliefs-box p {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(17px,2.3vw,21px); color:var(--text-dark);
  margin-bottom:14px; line-height:1.55;
}
.beliefs-box p:last-child { font-family:'Lato',sans-serif; font-style:normal; font-size:15px; color:var(--text-muted); margin-bottom:0; }

/* ══════════════════════════════════════════
   SECTION 5 — ALTERNATIVES (❌ cards)
══════════════════════════════════════════ */
.alt-card {
  display:flex; gap:18px; align-items:flex-start;
  padding:24px 0; border-bottom:1px solid rgba(0,0,0,0.06);
}
.alt-card:last-child { border-bottom:none; }
.alt-icon { font-size:22px; flex-shrink:0; margin-top:2px; }
.alt-title { font-family:'Cormorant Garamond',serif; font-size:clamp(18px,2.5vw,22px); color:var(--text-dark); margin-bottom:8px; font-weight:600; }

/* ══════════════════════════════════════════
   SECTION 6 — 3 LAWS (DARK GREEN)
══════════════════════════════════════════ */
.law-card {
  background:rgba(255,255,255,0.05);
  border:1px solid rgba(201,168,76,0.18);
  padding:36px; margin-bottom:24px;
  position:relative;
}
.law-card-num {
  font-family:'Cormorant Garamond',serif;
  font-size:80px; font-weight:300;
  color:rgba(201,168,76,0.08);
  position:absolute; top:12px; right:20px;
  line-height:1; user-select:none;
}
.law-card-tag {
  display:flex; align-items:center; gap:12px; margin-bottom:16px;
}
.law-card-label {
  font-size:10px; letter-spacing:.3em; text-transform:uppercase;
  color:var(--gold); font-family:'Tajawal',sans-serif; font-weight:500;
}
.law-h {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(22px,3.5vw,34px); font-weight:400;
  color:var(--white); line-height:1.2; margin-bottom:20px;
}
.law-body { font-size:clamp(15px,1.8vw,17px); color:rgba(232,228,220,0.8); font-weight:300; line-height:1.85; margin-bottom:16px; }
.law-beliefs {
  background:rgba(0,0,0,0.2); border-left:2px solid rgba(201,168,76,0.35);
  padding:16px 20px; margin:16px 0;
}
.law-beliefs p { font-size:14px; color:rgba(232,204,122,0.65); font-style:italic; margin-bottom:8px; }
.law-beliefs p:last-child { margin-bottom:0; }
.law-open-loop { font-style:italic; color:var(--gold-lt); font-size:14px; opacity:.8; }

.dark-p { color:rgba(232,228,220,0.8); font-size:clamp(16px,1.9vw,18px); font-weight:300; line-height:1.85; margin-bottom:22px; }

.ayah-box-dark {
  border-top:1px solid rgba(201,168,76,0.2);
  border-bottom:1px solid rgba(201,168,76,0.2);
  padding:24px; margin:20px 0; text-align:center;
  background:rgba(201,168,76,0.05);
}
.ayah-box-dark .ayah-ar { color:var(--gold-lt); }
.ayah-box-dark .ayah-en { color:rgba(200,192,178,0.85); }
.ayah-box-dark .ayah-src { color:var(--gold); }

/* ══════════════════════════════════════════
   SECTION 7 — YOUR STORY
══════════════════════════════════════════ */
.author-row {
  display:flex; gap:28px; align-items:flex-start;
  margin-bottom:32px;
}
.author-info { flex:1; }
.author-name { font-family:'Cormorant Garamond',serif; font-size:26px; color:var(--text-dark); margin-bottom:4px; }
.author-title { font-size:11px; letter-spacing:.2em; text-transform:uppercase; color:var(--gold); display:block; margin-bottom:0; }

/* ══════════════════════════════════════════
   SECTION 8 — TESTIMONIALS (PALE CREAM)
══════════════════════════════════════════ */
.testi-section-head {
  text-align:center; margin-bottom:52px;
}
.testi-card {
  text-align:center; max-width:580px; margin:0 auto 8px;
  padding:28px 24px;
}
.testi-stars { color:var(--gold); font-size:16px; margin-bottom:14px; }
.testi-quote {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(18px,2.5vw,22px); color:var(--text-dark);
  line-height:1.5; margin-bottom:14px; font-weight:300;
}
.testi-attr { font-size:12px; color:var(--gold); letter-spacing:.1em; text-transform:uppercase; }

/* ══════════════════════════════════════════
   SECTION 9 — PRODUCT REVEAL
══════════════════════════════════════════ */
.product-reveal { text-align:center; margin-bottom:60px; }
.product-title {
  font-family:'Tajawal',sans-serif; font-size:14px;
  letter-spacing:.25em; text-transform:uppercase;
  color:var(--gold); margin-bottom:12px; display:block;
}
.product-name {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(28px,5vw,52px); font-weight:300;
  color:var(--text-dark); line-height:1.15; margin-bottom:24px;
}

/* WEEK CARDS */
.week-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:2px; margin:40px 0; }
.week-card { padding:32px 28px; }
.week-card-1 { background:#EEF5F1; border-top:3px solid #52B788; }
.week-card-2 { background:#EEF0F8; border-top:3px solid #5B7EC5; }
.week-card-3 { background:#FDF8EC; border-top:3px solid var(--gold); }
.week-card-4 { background:#FDF3E8; border-top:3px solid #D4845A; }
.week-emoji { font-size:28px; margin-bottom:10px; display:block; }
.week-label { font-size:9px; letter-spacing:.3em; text-transform:uppercase; color:var(--text-muted); display:block; margin-bottom:6px; }
.week-title { font-family:'Cormorant Garamond',serif; font-size:clamp(18px,2.5vw,22px); color:var(--text-dark); margin-bottom:14px; font-weight:600; }
.week-body { font-size:14px; color:var(--text-muted); line-height:1.75; margin:0; }

/* ══════════════════════════════════════════
   SECTION 10 — BONUSES
══════════════════════════════════════════ */
.bonus-card {
  display:grid; grid-template-columns:220px 1fr;
  gap:0; background:var(--white);
  border:1px solid rgba(201,168,76,0.12);
  margin-bottom:4px; transition:border-color .3s; overflow:hidden;
}
.bonus-card:hover { border-color:rgba(201,168,76,0.3); }
.bonus-card-img {
  width:220px; height:100%; min-height:200px;
  object-fit:cover; object-position:center;
  display:block; flex-shrink:0;
  border-right:1px solid rgba(201,168,76,0.12);
}
.bonus-card-img-placeholder {
  width:220px; min-height:200px;
  background:linear-gradient(135deg,#1a2e22,#0f1f18);
  display:flex; align-items:center; justify-content:center;
  flex-shrink:0; border-right:1px solid rgba(201,168,76,0.12);
  font-size:40px;
}
.bonus-card-body {
  padding:28px 32px; display:flex; flex-direction:column; justify-content:center;
}
.bonus-num {
  font-family:'Cormorant Garamond',serif; font-size:11px; font-weight:400;
  color:rgba(201,168,76,0.5); letter-spacing:.25em; text-transform:uppercase;
  margin-bottom:8px; display:block;
}
.bonus-name { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--text-dark); margin-bottom:10px; font-weight:400; line-height:1.2; }
.bonus-desc { font-size:14px; color:var(--text-muted); margin:0; line-height:1.7; }

/* ══════════════════════════════════════════
   SECTION 11 — PRICE
══════════════════════════════════════════ */
.comp-table { margin:32px 0; }
.comp-row {
  display:flex; justify-content:space-between; align-items:baseline;
  padding:14px 0; border-bottom:1px solid rgba(0,0,0,0.06); gap:20px;
}
.comp-row:last-child { border-bottom:none; }
.comp-item { font-size:15px; color:var(--text-muted); flex:1; }
.comp-price { font-family:'Cormorant Garamond',serif; font-size:17px; color:var(--text-muted); font-style:italic; white-space:nowrap; }

.price-box {
  background:var(--white); border:2px solid var(--gold);
  padding:60px 48px; text-align:center; margin:40px 0;
  position:relative; overflow:hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
}
.price-box::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,transparent,var(--gold),transparent);
}
.price-was {
  font-size:12px; letter-spacing:.15em; color:var(--text-muted);
  text-decoration:line-through; text-transform:uppercase; margin-bottom:4px;
}
.price-anchor { font-size:14px; color:var(--text-muted); margin-bottom:20px; font-style:italic; font-family:'Cormorant Garamond',serif; }
.price-big {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(72px,14vw,108px); font-weight:300;
  color:var(--gold); line-height:1; margin-bottom:6px;
}
.price-sub { font-size:14px; color:var(--text-muted); margin-bottom:40px; letter-spacing:.08em; }
.price-note { font-size:13px; color:rgba(201,168,76,0.45); margin-top:14px; margin-bottom:0; }

/* ══════════════════════════════════════════
   SECTION 12 — GUARANTEE
══════════════════════════════════════════ */
.guarantee-box {
  border:1px solid rgba(82,183,136,0.3);
  background:var(--pale-green);
  padding:52px 48px; text-align:center; margin:0;
}
.guar-title {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(22px,4vw,32px); font-weight:400;
  color:var(--text-dark); margin-bottom:20px;
}
.guar-text { font-size:clamp(16px,1.9vw,18px); color:var(--text-body); max-width:580px; margin:0 auto 16px; }
.guar-kicker {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:18px; color:var(--emerald); margin-bottom:0; margin-top:16px;
}

/* ══════════════════════════════════════════
   SECTION 13 — WHO IT'S FOR
══════════════════════════════════════════ */
.for-grid { display:grid; grid-template-columns:1fr 1fr; gap:32px; margin-top:36px; }
.check-list { list-style:none; }
.check-list li {
  display:flex; gap:12px; align-items:flex-start;
  padding:12px 0; border-bottom:1px solid rgba(0,0,0,0.06);
  font-size:clamp(15px,1.8vw,17px); color:var(--text-body); line-height:1.6;
}
.check-list li:last-child { border-bottom:none; }
.check-icon { font-size:16px; flex-shrink:0; margin-top:2px; }

/* ══════════════════════════════════════════
   SECTION 14 — URGENCY
══════════════════════════════════════════ */
.urgency-box {
  background:rgba(201,168,76,0.07);
  border:1px solid rgba(201,168,76,0.2);
  padding:32px 36px; text-align:center; margin-top:32px;
}
.urgency-box p {
  font-family:'Cormorant Garamond',serif; font-style:italic;
  font-size:clamp(18px,2.5vw,22px); color:var(--text-dark); margin-bottom:0;
}

/* ══════════════════════════════════════════
   SECTION 15 — FAQ
══════════════════════════════════════════ */
.faq-item { border-bottom:1px solid rgba(0,0,0,0.07); padding:28px 0; }
.faq-item:last-child { border-bottom:none; }
.faq-from { font-size:10px; letter-spacing:.2em; text-transform:uppercase; color:rgba(201,168,76,0.6); margin-bottom:8px; display:block; }
.faq-q {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(18px,2.5vw,22px); color:var(--text-dark);
  margin-bottom:14px; font-weight:600; line-height:1.3;
}
.faq-a { font-size:15px; color:var(--text-muted); margin:0; line-height:1.85; }

/* ══════════════════════════════════════════
   CTA
══════════════════════════════════════════ */
.cta-block { text-align:center; margin:32px 0; }
.cta-btn {
  display:inline-block;
  background:linear-gradient(135deg,#C9A84C,#A07830,#C9A84C);
  background-size:200% 200%; animation:shimmer 3s ease infinite;
  color:#0B1812; font-family:'Tajawal',sans-serif;
  font-size:clamp(14px,1.8vw,16px); font-weight:800;
  letter-spacing:.15em; text-transform:uppercase;
  padding:20px 52px; text-decoration:none; cursor:pointer; border:none;
  transition:transform .2s,box-shadow .2s; line-height:1.3;
}
.cta-btn:hover { transform:translateY(-2px); box-shadow:0 12px 40px rgba(201,168,76,0.3); }
.cta-btn-dark {
  box-shadow:0 0 40px rgba(201,168,76,0.2);
}
.cta-sub { font-size:12px; color:var(--text-muted); margin-top:12px; }
.stars-row { margin-top:10px; display:flex; align-items:center; justify-content:center; gap:8px; }
.stars { color:var(--gold); font-size:15px; }
.stars-text { font-size:13px; color:var(--text-muted); }

@keyframes shimmer { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }

/* DARK CTA */
.cta-sub-dark { color:rgba(200,192,178,0.6); }
.stars-text-dark { color:rgba(200,192,178,0.5); }

/* ══════════════════════════════════════════
   FINAL SECTION
══════════════════════════════════════════ */
.final-inner {
  max-width:720px; margin:0 auto;
  padding:80px 24px 90px; text-align:center; position:relative; z-index:1;
}
.final-arabic { font-family:'Tajawal',sans-serif; font-size:clamp(26px,5vw,44px); color:var(--gold); direction:rtl; margin-bottom:10px; opacity:.9; }
.final-trans { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:16px; color:rgba(200,192,178,0.6); margin-bottom:56px; }
.final-h {
  font-family:'Cormorant Garamond',serif;
  font-size:clamp(28px,5.5vw,54px); font-weight:300;
  color:var(--white); line-height:1.15; margin-bottom:28px;
}
.final-h em { font-style:italic; color:var(--gold-lt); }
.final-p { font-size:clamp(16px,2vw,18px); color:rgba(200,192,178,0.7); max-width:560px; margin:0 auto 48px; }
.final-disclaimer { font-size:12px; color:rgba(200,192,178,0.35); margin-top:32px; max-width:480px; margin-left:auto; margin-right:auto; }

/* FOOTER */
footer {
  background:var(--white); border-top:1px solid rgba(0,0,0,0.07);
  padding:40px 24px; text-align:center;
  font-size:12px; color:var(--text-muted); line-height:2;
}
footer a { color:var(--text-muted); text-decoration:none; }
footer a:hover { color:var(--gold); }


/* ══════════════════════════════════════════
   DM TESTIMONIAL DROPS
══════════════════════════════════════════ */
.dm-drop {
  max-width:700px; margin:0 auto; padding:0 24px;
}
.dm-drop-wide {
  max-width:900px; margin:0 auto; padding:0 24px;
}
.dm-grid {
  display:grid; gap:16px;
}
.dm-grid-2 { grid-template-columns:1fr 1fr; }
.dm-grid-3 { grid-template-columns:1fr 1fr 1fr; }
.dm-card {
  border-radius:4px; overflow:hidden;
  border:1px solid rgba(201,168,76,0.2);
  box-shadow:0 4px 24px rgba(0,0,0,0.18);
  transition:transform .2s, box-shadow .2s;
}
.dm-card:hover { transform:translateY(-3px); box-shadow:0 8px 32px rgba(0,0,0,0.25); }
.dm-card img {
  width:100%; height:auto; display:block;
}
.dm-drop-label {
  text-align:center; margin-bottom:20px;
}
.dm-drop-label span {
  font-family:'Tajawal',sans-serif; font-size:10px;
  letter-spacing:.3em; text-transform:uppercase;
  color:rgba(201,168,76,0.5);
}
@media(max-width:680px){
  .dm-grid-2,.dm-grid-3 { grid-template-columns:1fr; }
}

/* FADE IN */
.fi { opacity:0; transform:translateY(22px); transition:opacity .7s ease,transform .7s ease; }
.fi.on { opacity:1; transform:translateY(0); }

/* RESPONSIVE */
@media(max-width:680px) {
  .sbar { flex-direction:column; gap:10px; text-align:center; }
  .for-grid { grid-template-columns:1fr; }
  .week-grid { grid-template-columns:1fr; }
  .author-row { flex-direction:column; gap:16px; }
  .price-box,.guarantee-box { padding:32px 18px; }
  .comp-row { flex-direction:column; gap:2px; }
  .comp-price { font-size:14px; }
  .bonus-card { grid-template-columns:1fr; }
  .bonus-card-img { width:100%; height:180px; border-right:none; border-bottom:1px solid rgba(201,168,76,0.12); }
  .bonus-card-img-placeholder { width:100%; border-right:none; border-bottom:1px solid rgba(201,168,76,0.12); }
  .bonus-card-body { padding:20px 18px; }
}
`;

export default function App() {
  const progRef = useRef(null);
  const sbarRef = useRef(null);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    const onScroll = () => {
      const s = window.scrollY;
      const d = document.documentElement.scrollHeight - window.innerHeight;
      if (progRef.current) progRef.current.style.width = (s / d * 100) + '%';
      if (sbarRef.current) sbarRef.current.classList.toggle('on', s > 700);
    };
    window.addEventListener('scroll', onScroll);
    const obs = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) e.target.classList.add('on'); }),
      { threshold: 0.06 }
    );
    document.querySelectorAll('.fi').forEach(el => obs.observe(el));
    return () => { window.removeEventListener('scroll', onScroll); document.head.removeChild(style); };
  }, []);

  return (
    <>
      <div id="prog" ref={progRef} />

      {/* STICKY BAR */}
      <div className="sbar" ref={sbarRef}>
        <div className="sbar-txt">30 Days to Effortless Rizq — <strong>$299 one time</strong> · 60-Day Guarantee</div>
        <a href={STRIPE} className="sbar-btn">Claim Your Spot →</a>
      </div>

      <div className="announce">⚡ Limited Spots Open — Mohammed Personally Reviews Every Student's Progress ⚡</div>

      {/* ══════════════════════════════════════
          SECTION 1 — HERO (DARK GREEN)
      ══════════════════════════════════════ */}
      <div className="s-hero" style={{ position: 'relative' }}>

        {/* BACKGROUND: mosque image fills entire hero */}
        <img
          src="/hero-mosque.png"
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 20%',
            zIndex: 0,
          }}
        />
        {/* Dark overlay for readability */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
          background: 'linear-gradient(170deg, rgba(4,10,7,0.92) 0%, rgba(4,10,7,0.80) 40%, rgba(4,10,7,0.95) 100%)',
        }} />

        <div className="hero-inner fi" style={{ position: 'relative', zIndex: 2 }}>


          <h1 className="hero-headline">
            A Forgotten Quranic Protocol Is Helping Regular Muslims Get Rizq That <em>Finds Them</em> — Without Hustling, Without Burnout, and Without the Guilt That Keeps Most Believers Quietly Stuck.
          </h1>

          <p className="hero-subhead">
            "Over 1,000 Muslims around the world have used this simple 30-day practice to break through years of invisible rizq blocks — including a brother who felt stuck for 6 years before provision rushed in within weeks."
          </p>

          {/* Creator photo / video */}
          <div className="sound-note">🔊 Make Sure Your Sound Is Turned ON</div>
          <div className="vwrap">
            <iframe
              src="https://www.youtube.com/embed/2fY_qGNAFjo?rel=0&modestbranding=1"
              title="Effortless Rizq — Reach Mohammed"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <OrderBtn label="YES — Show Me How →" price={false} />
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 2 — STORY HOOK (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <p className="story-open">Picture this.</p>
          <p className="story-open">You're standing alone in a brand new city.</p>
          <p>No money.<br />No home.<br />No family around.<br />Not even a place to sleep tonight.</p>
          <p>All you own are the clothes on your back. That's it.</p>
          <p>This was real life for <strong>Abdur-Rahman ibn Awf (رضي الله عنه)</strong> when he came to Madinah. One of the greatest men to ever walk this earth — and he showed up with nothing. No savings. No backup plan. No one to lean on.</p>
          <p>But then something happened. Something that people are still talking about — 1,400 years later. Within just a few years...</p>

          <div className="fi" style={{ margin: '40px auto', maxWidth: 560 }}>
            <img
              src="/story-gold.png"
              alt="Ancient Islamic gold coins and treasure"
              className="themed-img"
              style={{
                width: '100%', height: 340,
                objectFit: 'cover', objectPosition: 'center 40%',
              }}
            />
          </div>

          <p>He became one of the richest men in all of Madinah. So rich that when he died — the gold he left behind was too heavy to carry. <strong>They had to chop it apart with axes just to move it.</strong></p>

          <div className="pq fi">
            <p>Let that sink in. How does a man go from owning nothing... to that?</p>
          </div>

          <p>He didn't have a business plan. He didn't have a "strategy." He didn't grind 18 hours a day. He had something better. <strong>He was lined up — perfectly — with how Allah made provision work.</strong></p>
          <p>And here's what most people miss: He wasn't special. The early Muslims — the Sahaba — all lived this way. They had a kind of rizq that most of us today can't even picture. Not because they worked harder. But because they knew something about how Allah's system of provision really works.</p>
          <p>Something that got lost along the way. Something that is still sitting right there in the Quran. Plain as day. Waiting for you to see it.</p>

          <div className="pq fi">
            <p>The same system that brought Abdur-Rahman ibn Awf everything he ever needed... is still running. Right now. For you.</p>
            <cite>The only question is: are you lined up with it — or working against it by mistake?</cite>
          </div>

          <div className="dm-drop" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-3">
            <div className="dm-card fi">
              <img src="/dm-got-the-job.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-dua-came.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-pure-gold.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 3 — THE PROBLEM (OFF-WHITE)
      ══════════════════════════════════════ */}
      <div className="s-off-white sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">Let me ask you something.<br /><em>Be honest with yourself.</em></h2>
          <p>You pray. You work hard. You make dua — sometimes late at night, with tears rolling down your face.</p>
          <p>But there's still something tight in your chest. A quiet fear you can't get rid of. A feeling like rizq is right there — but something you can't see keeps it just out of reach. Like there's a wall of glass between you and what Allah promised.</p>
          <p>So you push harder. You try another side hustle. You sign up for another course. You wake up earlier. You go to sleep later. And still — nothing changes. You feel like you're on a treadmill. Running fast. Going nowhere.</p>

          <div className="fi" style={{ margin: '36px auto', maxWidth: 560 }}>
            <img
              src="/problem-stuck.png"
              alt="Person overwhelmed at desk late at night"
              className="themed-img"
              style={{
                width: '100%', height: 320,
                objectFit: 'cover', objectPosition: 'center 20%',
                filter: 'brightness(0.88) saturate(0.65)',
              }}
            />
          </div>

          <div className="beliefs-box fi">
            <p>If this sounds like you, I need you to hear me.</p>
            <p>Nothing is wrong with you. You are not being punished. You are not unlucky. Allah has not forgotten you.</p>
            <p>What's really going on is something much more simple — and much more fixable — than anyone has told you.</p>
          </div>

          <p>You're living with what I call <strong>Rizq Misalignment.</strong> And once you see what it is — everything starts to make sense.</p>

          <div className="dm-drop" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-2">
            <div className="dm-card fi">
              <img src="/dm-week2-breakdown.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-life-changed-x100.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 4 — THE DIAGNOSIS (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <h2 className="sec-h2"><em>Rizq Misalignment</em></h2>

          <p>Rizq Misalignment is when a good Muslim is doing everything right on the outside... but carrying hidden beliefs on the inside that quietly block the provision Allah already wrote for them. It's not about sin. It's not about being lazy. It's not about weak iman. It's about beliefs that got planted inside you — without your say — from the day you were born.</p>
          <p>You came into this world with something called <strong>fitrah</strong>. A natural, built-in trust in Allah's care. As a baby, you didn't worry about provision. You cried, and milk came. You needed warmth, and arms held you. You didn't "grind." You didn't "hustle." You just received. That was your natural state.</p>
          <p>But then the world started to change you.</p>

          <div className="fi" style={{ margin: '40px auto', maxWidth: 620 }}>
            <img
              src="/fitrah-dunya.png"
              alt="Aligned with Allah vs overwhelmed by dunya"
              className="themed-img"
              style={{
                width: '100%', height: 340,
                objectFit: 'cover', objectPosition: 'center center',
              }}
            />
          </div>

          <p>Your parents — who loved you more than anything — but who worried about money all the time. You watched them. You soaked it in. You learned, without words: provision is scary and hard to get. School and society taught you that winners hustle. Losers rest. Even the Muslim community — without meaning to — started measuring your worth by your job, your car, your house, your bank account.</p>
          <p>And bit by bit... your fitrah got buried under a pile of lies you never chose:</p>

          <div className="beliefs-box fi">
            <p>"If I don't push myself to the bone, I'll fall behind."</p>
            <p>"Rizq only comes to people who grind harder than everyone else."</p>
            <p>"If I slow down or trust, everything will fall apart."</p>
            <p>"Maybe Allah is holding back from me. Maybe I'm not ready."</p>
            <p>You don't think these thoughts on purpose. They run in the background. Like a bad app draining your phone battery without you knowing.</p>
          </div>

          <p>And they create one result: you work harder and harder. But the barakah never lands. You make dua. But it feels like it hits the ceiling and bounces back. You see others getting blessed. And you wonder: "Why not me?"</p>

          <div className="pq fi">
            <p>You can't out-hustle a broken belief. You can't out-work a heart that secretly thinks Allah is withholding. You can't force open doors that only open through surrender.</p>
          </div>

          <p>The Sahaba knew this. Abdur-Rahman ibn Awf knew this. And once you fix this... rizq doesn't just "come." <em>It chases you.</em></p>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 5 — ALTERNATIVES (PALE GREEN)
      ══════════════════════════════════════ */}
      <div className="s-pale-green sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">You've Already Tried<br /><em>To Fix This.</em></h2>
          <p>If you've been stuck in Rizq Misalignment, chances are you've already tried to fix things. But nothing worked. At least not deep down. Not in a way that lasted. Here's why.</p>

          <div style={{ marginTop: 32 }}>
            {[
              { icon: "❌", title: "You tried working harder.", body: "More hours. More projects. Maybe even a second job. But effort without alignment just makes you tired. You burned out — and the barakah still didn't show. Because rizq was never about how much you work. It was always about how aligned you are while you work." },
              { icon: "❌", title: "You tried learning more about Islam.", body: "You watched lectures. You read books. You followed scholars online. And that's good. But knowing something in your head is not the same as living it in your heart. You can know that Allah is Ar-Razzaq. And still feel — deep in your gut — that you need to panic about money. That gap between what you know and what you feel? That's where Rizq Misalignment lives." },
              { icon: "❌", title: "You tried making more dua.", body: "And dua is real. Dua is powerful. No doubt. But dua from a heart full of hidden fear... is like planting good seeds in bad soil. The seeds are fine. The soil is the problem. Until you fix what's underneath, even the most beautiful dua can feel like it's not landing." },
              { icon: "❌", title: "You tried courses and hustle programs.", body: 'The Instagram business guy. The "build your empire" course. The productivity hack. Some had good tips. But none of them touched the real issue. Because the real issue isn\'t about tactics or strategy. It\'s about what\'s happening inside your chest. It\'s spiritual.' },
              { icon: "❌", title: "You tried \"just trusting Allah\" — but your body wouldn't listen.", body: 'You told yourself: "Have tawakkul." You wanted to trust. But your chest stayed tight. Your sleep stayed broken. Your fear stayed loud. Because tawakkul is not something you can just think into being. Your whole self needs to enter that state. And that takes a real process.' },
            ].map((a, i) => (
              <div className="alt-card fi" key={i}>
                <span className="alt-icon">{a.icon}</span>
                <div>
                  <div className="alt-title">{a.title}</div>
                  <p style={{ fontSize: 15, color: 'var(--text-muted)', marginBottom: 0 }}>{a.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 36 }}>
            <p>None of these things are bad. They're just not enough on their own. They treat what's on the surface. They don't touch the root. The root is Rizq Misalignment. The fix is a <strong>Rizq Realignment</strong> — a step-by-step process that puts you back in line with how Allah designed provision to flow. That process exists. It's been in the Quran this whole time.</p>
          </div>


          <div className="dm-drop" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-3">
            <div className="dm-card fi">
              <img src="/dm-let-go-money-came.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-acceptance-allah.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-no-one-teaching.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>

          {/* CTA #1 — Early deciders */}
          <div style={{ marginTop: 48 }} className="fi">
            <OrderBtn label="YES — I Want This For Myself →" price={false} />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 6 — 3 DIVINE LAWS (DARK GREEN)
      ══════════════════════════════════════ */}
      <div className="s-dark sec-pad-lg">

        {/* Quran image — vertical photo, centered and contained */}
        <div className="fi" style={{ display:'flex', justifyContent:'center', padding:'0 24px', paddingTop:60 }}>
          <img
            src="/quran-candle.jpg"
            alt="Open Quran with candle and prayer beads"
            className="themed-img"
            style={{
              width: '100%',
              maxWidth: 420,
              height: 480,
              objectFit: 'cover',
              objectPosition: 'center 30%',
              filter: 'brightness(0.95)',
            }}
          />
        </div>

        <div className="col" style={{ paddingTop: 64 }}>
          <div className="fi">
              <h2 className="sec-h2 sec-h2-dark">The 3 Divine Laws<br /><em>of Rizq</em></h2>
            <p className="dark-p">The Quran has an operating system for provision. Not a poetic idea. A real system. Allah put laws into His creation that control how rizq flows — and how it gets blocked. These laws are as real as gravity. They don't play favorites. They don't bend for anyone. They just respond to one thing: <strong style={{ color: 'var(--gold-lt)' }}>alignment.</strong></p>
            <p className="dark-p">When you're aligned — rizq flows. Easy. From places you never expected. When you're not aligned — rizq fights you. No matter how hard you push. Through years of study, personal testing, and working with over 1,000 Muslims... I've found 3 specific laws in the Quran that sit at the heart of rizq flow.</p>
          </div>

          {/* LAW 1 */}
          <div className="law-card fi">
            <div className="law-card-num">01</div>
            <div className="law-card-tag">
              <span style={{ fontSize: 28, mixBlendMode: 'screen' }}>📜</span>
              <span className="law-card-label">☝️ Divine Law #1</span>
            </div>
            <div className="law-h">The Law of Written Provision</div>
            <div className="ayah-box-dark">
              <div className="ayah-ar">وَفِي السَّمَاءِ رِزْقُكُمْ وَمَا تُوعَدُونَ</div>
              <div className="ayah-en">"And in the heaven is your provision and whatever you are promised."</div>
              <div className="ayah-src">Surah Adh-Dhariyat 51:22</div>
            </div>
            <p className="law-body">This law shows you that your rizq isn't "out there." It's not running away from you. It's not something you need to fight for, beg for, or lose sleep over. <strong style={{ color: 'var(--white)' }}>It's already written. Already set aside. Already on its way to you.</strong></p>
            <p className="law-body">But here's the part nobody teaches: there's a way you hold yourself on the inside — a posture — that either pulls rizq toward you... or pushes it away. Most Muslims carry the pushing posture their whole lives. They don't even know it.</p>
            <p className="law-open-loop">Inside the program, I'll show you what this posture looks like, how to spot it in yourself, and the simple daily practice that flips it — usually within the first 7 days.</p>
          </div>

          {/* LAW 2 */}
          <div className="law-card fi">
            <div className="law-card-num">02</div>
            <div className="law-card-tag">
              <div style={{ width: 36, height: 36, flexShrink: 0, background: '#000', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                <img src="/icon-eye.png" alt="" style={{ width: 36, height: 36, objectFit: 'contain', mixBlendMode: 'screen' }} />
              </div>
              <span className="law-card-label">☝️ Divine Law #2</span>
            </div>
            <div className="law-h">The Law of How You See Allah</div>
            <div className="ayah-box-dark">
              <div className="ayah-ar">أَنَا عِندَ ظَنِّ عَبْدِي بِي</div>
              <div className="ayah-en">"I am as My servant thinks of Me."</div>
              <div className="ayah-src">Hadith Qudsi — Sahih Bukhari & Muslim</div>
            </div>
            <p className="law-body">There's a Hadith Qudsi that holds the single most powerful key to unlocking rizq. It shows you that <strong style={{ color: 'var(--white)' }}>what you get from Allah is shaped by what you believe about Allah.</strong> Not what you say you believe. Not what you put on social media. What you actually believe — in the quiet of your heart at 2am when nobody's watching.</p>
            <div className="law-beliefs">
              <p>"Maybe He's testing me by making things hard..."</p>
              <p>"Maybe I need to suffer more before I'm good enough..."</p>
              <p>"Maybe others deserve ease, but not me..."</p>
            </div>
            <p className="law-open-loop">Inside the program, I'll walk you through a process to find these hidden beliefs, pull them out by the root, and replace them with how the Sahaba actually saw Allah. That shift alone has been the turning point for more students than I can count.</p>
          </div>

          {/* LAW 3 */}
          <div className="law-card fi">
            <div className="law-card-num">03</div>
            <div className="law-card-tag">
              <div style={{ width: 36, height: 36, flexShrink: 0, background: '#000', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                <img src="/icon-hand.png" alt="" style={{ width: 36, height: 36, objectFit: 'contain', mixBlendMode: 'screen' }} />
              </div>
              <span className="law-card-label">☝️ Divine Law #3</span>
            </div>
            <div className="law-h">The Law of Letting Go</div>
            <div className="ayah-box-dark">
              <div className="ayah-ar">وَمَن يَتَّقِ اللَّهَ يَجْعَل لَّهُ مَخْرَجًا ۝ وَيَرْزُقْهُ مِنْ حَيْثُ لَا يَحْتَسِبُ</div>
              <div className="ayah-en">"Whoever fears Allah — He will make for him a way out, and provide for him from where he does not expect."</div>
              <div className="ayah-src">Surah At-Talaq 65:2-3</div>
            </div>
            <p className="law-body">This is the law most Muslims find hardest to understand. And the most life-changing once they get it. <strong style={{ color: 'var(--white)' }}>The harder you grip provision, the more it slips through your fingers.</strong> The more you let go — not your effort, but your desperate need to control the outcome — the faster it comes.</p>
            <p className="law-body">This isn't about sitting at home doing nothing. It's about doing your best work with full heart... while trusting that the results are in Allah's hands, not yours. The Sahaba mastered this. That's why they could lose everything and have it all back in months.</p>
            <p className="law-open-loop">Inside the program, I'll teach you the daily practice that trains your heart to live this way. And the change in barakah that follows — students tell me — is almost instant.</p>
          </div>

          <div className="fi" style={{ marginTop: 24 }}>
            <div className="pq pq-dark">
              <p>When all three laws are running in your life — your beliefs, your view of Allah, and your grip on outcomes all lined up — you enter what I call <em>Rizq Flow.</em> It's how Abdur-Rahman ibn Awf lived. It's how the Sahaba moved through the world.</p>
            </div>
          </div>


          <div className="dm-drop-wide" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-3">
            <div className="dm-card fi">
              <img src="/dm-life-changed-x100.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-dua-came.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-22k-trust.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>

          {/* CTA #2 */}
          <div className="fi" style={{ marginTop: 48 }}>
            <div className="cta-block">
              <a href={STRIPE} className="cta-btn cta-btn-dark">YES — I'm Ready to Realign → $299</a>
              <p className="cta-sub cta-sub-dark">Secure Checkout · Instant Access · 60-Day Guarantee</p>
              <div className="stars-row">
                <span className="stars">⭐⭐⭐⭐⭐</span>
                <span className="stars-text stars-text-dark">Over 1,000 Muslims Enrolled</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 7 — YOUR STORY (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">I Know This Works Because<br /><em>I Lived the Opposite.</em></h2>

          <div className="author-row">
            <img
              src="/author-photo.jpg"
              alt="Reach Mohammed at the Kaaba, Makkah"
              style={{
                width: 100, height: 100,
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: 'center 15%',
                flexShrink: 0,
                border: '2px solid rgba(201,168,76,0.4)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              }}
            />
            <div className="author-info">
              <div className="author-name">Reach Mohammed</div>
              <span className="author-title">Creator of 30 Days to Effortless Rizq</span>
            </div>
          </div>

          <p>A few years ago, I was buried under $20,000 of debt. Working all the time. Barely sleeping. Making dua every single night. But every morning — the same weight on my chest. I knew that Allah is Ar-Razzaq. I could quote the ayah. But I couldn't feel it. My body was stuck in panic mode.</p>
          <p>And no video, no lecture, no amount of trying harder was changing that. Until I stopped trying to learn more — and started trying to realign instead.</p>

          <div className="fi" style={{ margin: '36px auto', maxWidth: 500 }}>
            <img
              src="/author-photo.jpg"
              alt="Reach Mohammed at the Kaaba, Makkah"
              className="themed-img"
              style={{
                width: '100%',
                height: 420,
                objectFit: 'cover',
                objectPosition: 'center 20%',
              }}
            />
          </div>

          <p>I went back to the Quran. Not to read more tafsir. But to pull out the actual operating rules of provision. And when I found these 3 laws — and started living by them, day by day... the shift came.</p>
          <p>It was quiet at first. A feeling of peace in my chest I hadn't felt in years. A dua that — for the first time — felt like it actually went somewhere. A phone call with a chance I wasn't looking for. Then the quiet got loud.</p>

          <div className="pq fi">
            <p>From $20,000 in debt... to over $700,000 in provision. Without a new plan. Without pushing harder. Just alignment.</p>
            <cite>But here's what matters more than the money: I stopped waking up afraid.</cite>
          </div>

          <p>I stopped gripping. I started trusting — really trusting — that what was written for me would find me. And it did. That peace is the real gift. The provision is what comes after.</p>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 8 — TESTIMONIALS (PALE AMBER)
      ══════════════════════════════════════ */}
      <div className="s-pale-amber sec-pad">
        <div className="col-wide fi">
          <div className="testi-section-head">
            <span className="sec-label">What Others Are Saying</span>
            <h2 className="sec-h2 sec-h2-center">What People<br /><em>Are Experiencing</em></h2>
          </div>

          {[
            { quote: "I feel like I was holding my breath for six years. And I finally let it out. Doors that stayed shut for years opened without me even pushing.", attr: "Brother M., United Kingdom" },
            { quote: "I stopped working myself to death. I stopped carrying that guilt. And for the first time in my life, I felt like Allah actually wanted to give me more. The provision followed within weeks.", attr: "Sister A., Canada" },
            { quote: "I'm not even Muslim. I heard about these ideas from a video and applied them. Within a few months my entire financial life changed. This works.", attr: "D., Europe" },
            { quote: "My salah changed. My dua changed. My relationship with Allah changed. The money was just a side effect.", attr: "Brother Y., United States" },
            { quote: "In the first week, I felt something lift off my chest. By week three, I had my first $10,000 day. I didn't try harder. I just stopped blocking what was already mine.", attr: "Brother K., United States" },
            { quote: "This isn't a course. It's a spiritual reset. I've spent thousands on programs. This is the only one that touched the root.", attr: "Sister F., Australia" },
          ].map((t, i) => (
            <div key={i} className="fi">
              <div className="testi-card">
                <div className="testi-stars">⭐⭐⭐⭐⭐</div>
                <div className="testi-quote">"{t.quote}"</div>
                <div className="testi-attr">— {t.attr}</div>
              </div>
              {i < 5 && <Divider />}
            </div>
          ))}

          {/* CTA #3 */}
          <div className="fi" style={{ marginTop: 48 }}>
            <OrderBtn label="YES — I'm Ready to Realign →" price={true} />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 9 — PRODUCT REVEAL (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <p>So you might be thinking: <em>"Okay, I get it. But how do I actually do this?"</em></p>
          <p>That's the question that made me build this. Because understanding Rizq Misalignment in your head isn't enough. You need a daily practice. A step-by-step path. Something that changes you from the inside out — not in theory, but in your real life.</p>

          <div className="product-reveal fi">
            <span className="product-title">That's what this is:</span>
            <div className="product-name">🌙 30 Days to Effortless Rizq</div>
            <div style={{ margin: '28px auto', maxWidth: 700 }}>
              <img
                src="/product-mockup.png"
                alt="30 Days to Effortless Rizq — full program mockup on laptop, tablet, phone and book"
                className="themed-img"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>

          <p>A Quranic system that clears the hidden blocks between you and the provision Allah already wrote for you. For 30 days, you follow a simple guided practice. Each day takes about 10 to 15 minutes. Each day builds on the day before. And each day moves you deeper into alignment with how Allah made provision work.</p>
          <p><strong>By Day 30, you won't just understand how rizq works. You'll feel it. In your chest. In your day. In the things that start showing up in your life.</strong></p>
        </div>

        <div className="col-wide" style={{ paddingTop: 48 }}>
          <div className="week-grid fi">
            {[
              { emoji: "📖", week: "Week 1 — Days 1–7", color: "week-card-1", title: "The Rizq Reset", sub: "Activating Divine Law #1", body: "In the first week, we find the invisible chains. You'll uncover the exact scarcity beliefs running in your mind — the ones you picked up from your parents, your culture, and the world around you. And you'll start pulling them out — one by one. By the end of Week 1, you'll feel something shift. Most students say it's a real, physical thing — like a weight being lifted off their chest. You'll start to notice small signs of barakah almost immediately." },
              { emoji: "🧠", week: "Week 2 — Days 8–14", color: "week-card-2", title: "The Belief Rewiring", sub: "Activating Divine Law #2", body: "This is the deepest week. You'll find the hidden beliefs you carry about Allah — the quiet doubts you would never say out loud. Then, through a daily guided practice, you'll rewire how you see Allah. Not just in your mind. In your gut. In your bones. This is the week most students get their first big surprise. The dua that gets answered out of nowhere. The client who shows up without being chased." },
              { emoji: "🌊", week: "Week 3 — Days 15–21", color: "week-card-3", title: "Flow State", sub: "Activating Divine Law #3", body: "In Week 3, you learn to work without gripping. To give your full effort — while leaving the results completely to Allah. You'll feel the difference between work with barakah (light, joyful, almost easy) and work without barakah (heavy, draining, never enough). By the end of Week 3, other people will notice a change. Less rush. More calm. Less worry. More trust." },
              { emoji: "🏡", week: "Week 4 — Days 22–30", color: "week-card-4", title: "Coming Home", sub: "Locking It All In", body: "In the last week, we make this permanent. The 3 Divine Laws get locked into your daily life — not as something you try to remember, but as something you just are. You'll reconnect with the truth the Sahaba never forgot: this dunya is not your home. You stop living like someone begging for crumbs. You start living like someone who's already been taken care of." },
            ].map((w, i) => (
              <div className={`week-card ${w.color} fi`} key={i}>
                <span className="week-emoji">{w.emoji}</span>
                <span className="week-label">{w.week} · {w.sub}</span>
                <div className="week-title">{w.title}</div>
                <p className="week-body">{w.body}</p>
              </div>
            ))}
          </div>


          <div className="dm-drop" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-2">
            <div className="dm-card fi">
              <img src="/dm-10k-week2.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-week2-breakdown.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-week1-closer-allah.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-two-weeks-calmer.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>

          {/* CTA — after weeks */}
          <div className="fi" style={{ paddingTop: 48 }}>
            <OrderBtn label="YES — I'm Ready to Realign →" price={true} />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 10 — BONUSES (LIGHT CREAM)
      ══════════════════════════════════════ */}
      <div className="s-cream2 sec-pad">
        <div className="col fi" style={{ marginBottom: 40 }}>
          <span className="sec-label">When You Join Today</span>
          <h2 className="sec-h2">You Also Get<br /><em>These 4 Gifts</em></h2>
          <p>Each one is made to protect and deepen your Rizq Realignment:</p>
        </div>
        <div className="col-wide">
          {/* GIFT 1 */}
          <div className="bonus-card fi">
            <img src="/gift-1-reset.png" alt="Emergency Reset" className="bonus-card-img" />
            <div className="bonus-card-body">
              <span className="bonus-num">Gift 01</span>
              <div className="bonus-name">🎁 The Emergency Reset</div>
              <p className="bonus-desc">When scarcity sneaks back in — clear it in minutes. Life will test you. A tough month. A scary bill. A wave of doubt. This is a 5-step practice you can use any time the old feelings come back. Think of it as a reset button for your Rizq Flow. I use it myself. It works fast.</p>
            </div>
          </div>

          {/* GIFT 2 */}
          <div className="bonus-card fi">
            <img src="/gift-2-journal.png" alt="40-Day Barakah Dua Journal" className="bonus-card-img" />
            <div className="bonus-card-body">
              <span className="bonus-num">Gift 02</span>
              <div className="bonus-name">🎁 The 40-Day Barakah Dua Journal</div>
              <p className="bonus-desc">A guided dua practice that builds your connection to Allah day by day. This isn't just a list of duas. It's a 40-day guided journal that pairs Quranic duas with the belief-rewiring work from the main program. Students call this "the gift that changed my salah."</p>
            </div>
          </div>

          {/* GIFT 3 */}
          <div className="bonus-card fi">
            <img src="/gift-3-fieldguide.png" alt="Rizq Flow Field Guide" className="bonus-card-img" />
            <div className="bonus-card-body">
              <span className="bonus-num">Gift 03</span>
              <div className="bonus-name">🎁 The Rizq Flow Field Guide</div>
              <p className="bonus-desc">How to bring barakah into your daily work and choices. This guide shows you how to bring ihsan into your work, your money, and your daily life — so everything you touch carries barakah. Includes simple tools for making big choices from a place of trust and reading the signs of divine timing.</p>
            </div>
          </div>

          {/* GIFT 4 */}
          <div className="bonus-card fi">
            <img src="/gift-4-circle.png" alt="Complete Your Rizq Circle" className="bonus-card-img" />
            <div className="bonus-card-body">
              <span className="bonus-num">Gift 04</span>
              <div className="bonus-name">🎁 Complete Your Rizq Circle — Attracting the Right Spouse</div>
              <p className="bonus-desc">The biggest rizq choice you'll ever make. Allah didn't give Adam (AS) a job, a house, or a savings account. He gave him a spouse. This bonus shows you how to line up your heart and intention to attract (or build a stronger bond with) someone who protects your provision — instead of draining it.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 11 — PRICE (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">So Let Me Talk About<br /><em>The Investment.</em></h2>

          <p>But first, think about what you've already spent trying to fix this feeling. The courses that taught you tactics — but didn't touch the root. The books that inspired you for a week — then faded. The supplements, the apps, the coaching calls. Add it up. You know the number. For most people it's hundreds, sometimes thousands of dollars — and they're still stuck.</p>
          <p>Now think about the other costs. Not the money costs. The real costs. How many nights have you gone to bed with your chest tight, worrying about provision? How many years have you worked past the point of exhaustion — and still felt behind? How much barakah was written for you that couldn't reach you because of blocks you didn't even know were there? You can't put a dollar number on that. But you can feel the weight of it.</p>
          <p>Now look at what people normally pay for this kind of help:</p>

          <div className="comp-table fi">
            {[
              ["A single session with a Muslim life coach", "$150–$300 — one hour, then you're on your own"],
              ["A weekend Islamic retreat", "$500–$1,000 — then real life hits Monday and the feeling fades"],
              ["Ongoing therapy for deep scarcity beliefs", "$5,000–$10,000+ a year — most don't touch the spiritual root"],
              ["A personal mentor for a full month of daily guidance", "$2,000–$5,000 — if you can even find one"],
            ].map(([item, price], i) => (
              <div className="comp-row" key={i}>
                <span className="comp-item">{item}</span>
                <span className="comp-price">{price}</span>
              </div>
            ))}
          </div>

          <p>30 Days to Effortless Rizq gives you daily guided support for a full month. It addresses the exact root — the spiritual misalignment — that none of those other options touch. It comes with 4 extra gifts. And it's already helped over 1,000 people do what years of hustling and worrying couldn't.</p>

          <div className="price-box fi">
            <div className="price-was">Full Value: $997+</div>
            <div className="price-anchor">Your investment today — one time, yours forever</div>
            <div className="price-big">$299</div>
            <div className="price-sub">One time · Not monthly · Not yearly · Yours forever</div>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 40, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, serif' }}>
              That's about $10 a day for the 30 days of the program — less than what most people spend on coffee. Less than a single coaching session. Less than the courses you've already paid for that didn't touch the root.
            </p>
            {/* CTA #4 — Price section */}
            <a href={STRIPE} className="cta-btn">YES — I Want Effortless Rizq → $299</a>
            <p className="price-note">256-Bit SSL Secure · Instant Digital Access · Available Worldwide</p>
          </div>
        </div>
      </div>

      {/* DROP 7 — Price DMs */}
      <div className="s-white" style={{ paddingTop: 0, paddingBottom: 60 }}>

          <div className="dm-drop-wide" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-2">
            <div className="dm-card fi">
              <img src="/dm-5k-sales.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-10k-week.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-worth-every-penny.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-22k-trust.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 12 — GUARANTEE (PALE GREEN)
      ══════════════════════════════════════ */}
      <div className="s-pale-green sec-pad">
        <div className="col fi">
          <div className="guarantee-box">
            <img
              src="/guarantee-badge.png"
              alt="60-Day Barakah Guarantee"
              style={{
                width: 200, height: 200,
                display: 'block', margin: '0 auto 24px',
                objectFit: 'contain',
              }}
            />
            <div className="guar-title">And You Don't Even Need to Decide Right Now. Just say "maybe."</div>
            <p className="guar-text">Because your investment is fully protected.</p>
            <h3 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(20px,3vw,26px)', color: 'var(--emerald)', marginBottom: 20, fontWeight: 400 }}>The Barakah Guarantee — 60 Days</h3>
            <p className="guar-text">Go through the full 30 days. Follow the daily practice. Show up with an open heart. If by Day 60 you don't feel a real shift — more peace, more alignment, more trust in how Allah provides — just send me a message. Show me you did the work. And I'll give you every penny back.</p>
            <p className="guar-kicker">No forms. No guilt. No runaround. Just khayr.</p>
            <p className="guar-text" style={{ marginTop: 16, fontSize: 14, color: 'var(--text-muted)' }}>I can offer this because I've seen what happens when someone actually follows this process. I'm not worried. <strong>You risk nothing. You realign everything.</strong></p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 13 — WHO IT'S FOR (WHITE)
      ══════════════════════════════════════ */}
      <div className="s-white sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">Who This Is For —<br /><em>And Who It's Not</em></h2>
          <div className="for-grid">
            <div>
              <h3 className="sec-h3" style={{ color: 'var(--emerald)', marginBottom: 16 }}>This is for you if:</h3>
              <ul className="check-list">
                {[
                  "You pray and work hard, but there's still a tightness in your chest around provision that won't go away.",
                  "You've tried courses, lectures, and hustle plans — but nothing shifted the deep feeling of scarcity.",
                  "You're ready to stop chasing rizq and start receiving it — the way the early Muslims did.",
                  "You can give 10–15 minutes a day for 30 days to a process that could change the rest of your life.",
                  "You believe — even 1% — that Allah's system of provision is real, and that you might be out of line with it.",
                ].map((item, i) => (
                  <li key={i}><span className="check-icon">✅</span>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="sec-h3" style={{ color: '#A04040', marginBottom: 16 }}>This is NOT for you if:</h3>
              <ul className="check-list">
                {[
                  'You want a "get rich quick" trick. This is real change from the inside out — not a money hack.',
                  "You don't want to look at your own beliefs. This program asks you to be honest about things you've carried for years.",
                  "You want someone to tell you exactly what business to start. This is about becoming the kind of person provision flows toward.",
                ].map((item, i) => (
                  <li key={i}><span className="check-icon">❌</span>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 14 — URGENCY (PALE AMBER)
      ══════════════════════════════════════ */}
      <div className="s-pale-amber sec-pad-sm">
        <div className="col fi">
          <h2 className="sec-h2">I Personally Review<br /><em>Every Student's Progress</em></h2>
          <p>Not with a bot. Not with a team. Me. Because this work is personal. And I want to make sure every student gets real attention. That means I can only take a limited number of people at a time.</p>
          <p>Right now, there are spots open. But once they fill — and they do fill — I pause enrollment until I can give proper attention to the next group. I can't tell you exactly when this page will come down.</p>
          <div className="urgency-box">
            <p>If your heart is feeling something right now — if reading this page stirred something in your chest — that's not random. Don't let it pass.</p>
          </div>
          <div style={{ marginTop: 40 }}>
            <a href={STRIPE} className="cta-btn" style={{ display: 'block', textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>YES — I'm Ready to Realign → $299</a>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 15 — FAQ (WARM GRAY)
      ══════════════════════════════════════ */}
      <div className="s-warm-gray sec-pad">
        <div className="col fi">
          <h2 className="sec-h2">Still Thinking It Over?<br /><em>Let Me Answer That.</em></h2>
          <p>Since we put this program out, my inbox hasn't stopped. People from all over the world writing in with questions. So let me answer the ones that come up most while you're making your choice.</p>

          <div style={{ marginTop: 48 }}>
            {[
              { from: "Brother Tariq — East London", q: "I've bought Islamic courses before and they just collect dust. How is this any different?", a: "Great question, Tariq. Most courses give you more information to stuff into your head. More things to 'learn.' This isn't that. This is a daily guided practice. Each day takes 10–15 minutes. Each day builds on the last. You're not learning new facts — you're rewiring how your heart and mind relate to provision. Think of it this way: there's a big difference between reading about swimming and actually getting in the water. Most courses keep you on dry land. This one puts you in the water, day by day, until you're swimming on your own." },
              { from: "Sister Amina — Toronto", q: "I already pray five times a day and make dua regularly. Do I really need this?", a: "That's actually the perfect starting point, Amina. This program isn't about doing more. You're already doing a lot. The issue isn't your effort or your worship — it's the invisible blocks sitting between your effort and the results. Think of it like a clogged pipe. The water — your prayers, your work, your dua — is already flowing. We just need to clear what's blocking it. Many of our most successful students are people who were already praying, already working, already trying — and couldn't figure out why things still felt stuck. That's exactly who this was built for." },
              { from: "Brother Yusuf — Houston", q: "Is this really based on Quran and Sunnah? Or is it some kind of 'law of attraction' thing?", a: "I get this a lot, Yusuf. There's a lot of new-age stuff dressed up in Islamic language out there. This is not that. Every single part of Effortless Rizq is built on Quranic ayahs, sahih hadith, and the lived example of the Sahaba. Nothing is made up. Nothing is borrowed from outside of Islam. The 3 Divine Laws come straight from the Quran. The practices come from the Sunnah and the way of the early Muslims." },
              { from: "Sister Noor — Karachi", q: "I have a job and three kids. I barely have time to breathe. Can I realistically do this?", a: "Sister Noor, if you have 10–15 minutes a day — and I mean it, that's all it is — you can do this. Many of our students are busy parents, working professionals, and students with packed schedules. They do the daily practice during their morning coffee, right before Fajr, or in a quiet moment before bed. This isn't about adding more to your life. It's about changing the energy behind everything you're already doing." },
              { from: "Brother Omar — Dearborn", q: "I've been burned before by online programs. How do I know this actually works?", a: "I respect that, Omar. And honestly, I'd be worried if you weren't a little cautious. Here's what I can tell you: over 1,000 people have been through this program. The results speak. But more than that — you're protected by the 60-Day Barakah Guarantee. Go through the whole thing. Apply it. And if you don't feel a real shift, just message me and I'll give you your money back. No games, no runaround." },
              { from: "Sister Fatima — Birmingham", q: "Will this help me find a job? I've been unemployed for 8 months.", a: "Fatima, I can't promise you a specific job — that's in Allah's hands. What I can tell you is this: when you clear the scarcity beliefs, when you fix how you see Allah's provision, and when you start moving through life in Rizq Flow instead of panic mode — things start to change. Doors that stayed shut start to open. Chances you never expected show up. And you move through the world with a kind of calm confidence that people notice — including the people who make hiring decisions." },
              { from: "Brother Ismail — Cape Town", q: "I'm not in financial trouble. I'm doing okay. Is this still for me?", a: "Great question, Ismail. Rizq Misalignment isn't just about money trouble. It's about the quiet tightness that never fully goes away. The feeling that provision should be easier than it is — even though things look 'fine' on the outside. Some of our best results come from people who weren't broke. They were just stuck at a level that didn't match what they knew Allah could give them." },
              { from: "Brother Ahmed — Riyadh", q: "How fast do I get access?", a: "Instant, Ahmed. As soon as you complete your order, you get everything — the full 30-day program and all 4 gifts. It's all digital. You can start Day 1 today." },
            ].map((f, i) => (
              <div className="faq-item fi" key={i}>
                <span className="faq-from">{f.from}</span>
                <div className="faq-q">"{f.q}"</div>
                <p className="faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DROP 8 — Final emotional DMs */}
      <div className="s-warm-gray" style={{ paddingTop: 0, paddingBottom: 60 }}>

          <div className="dm-drop-wide" style={{ marginTop: 48, marginBottom: 0 }}>
          <div className="dm-drop-label fi"><span>Real Messages From Students</span></div>
            <div className="dm-grid dm-grid-2">
            <div className="dm-card fi">
              <img src="/dm-honey-never-poison.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-worth-every-penny.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-week1-rewatched.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            <div className="dm-card fi">
              <img src="/dm-week1-closer-allah.jpg" alt="Student testimonial" loading="lazy" />
            </div>
            </div>
          </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 16 — FINAL CTA (DARK GREEN)
      ══════════════════════════════════════ */}
      <div className="s-dark" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Desert sunset background */}
        <img
          src="/final-desert.jpg"
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 40%',
            zIndex: 0, opacity: 0.45,
          }}
        />
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(170deg, rgba(4,10,7,0.85) 0%, rgba(4,10,7,0.65) 40%, rgba(4,10,7,0.92) 100%)',
        }} />
        <div className="final-inner fi" style={{ position: 'relative', zIndex: 2 }}>
          <div className="final-arabic">إِنَّ مَعَ الْعُسْرِ يُسْرًا</div>
          <p className="final-trans">"Verily, with hardship comes ease." — Surah Al-Inshirah, 94:6</p>
          <h2 className="final-h">You're not broken.<br />You're not unlucky.<br /><em>You're not being punished.</em></h2>
          <p className="final-p">You've just been pulled out of alignment — by the dunya, by culture, by beliefs you never chose — from how Allah designed rizq to reach you. Now you have a way back. A clear, simple, 30-day path to getting back in line with the provision that was already written for you before you took your first breath.</p>
          <p className="final-p">The Sahaba walked this path. Over 1,000 Muslims have walked it through this program. Now it's your turn.</p>

          {/* CTA #5 — Final */}
          <div className="cta-block fi">
            <a href={STRIPE} className="cta-btn cta-btn-dark">YES — I'm Ready to Realign → $299</a>
            <p className="cta-sub cta-sub-dark">Secure Checkout · Instant Access · 60-Day Guarantee</p>
            <div className="stars-row">
              <span className="stars">⭐⭐⭐⭐⭐</span>
              <span className="stars-text stars-text-dark">Over 1,000 Muslims Enrolled</span>
            </div>
          </div>
          <p className="final-disclaimer">30 Days to Effortless Rizq is a digital program. You get instant access when you join. All content is online — nothing ships to your door.</p>
        </div>
      </div>

      <footer>
        <p>Disclaimer: Individual results will vary. This program is for educational and spiritual development purposes. It is not a substitute for professional financial, medical, or legal advice. Results shared by students are not guaranteed and reflect individual experiences.</p>
        <p style={{ marginTop: 8 }}>Copyright © 2026 Reach Mohammed · All rights reserved.</p>
        <p style={{ marginTop: 8 }}>
          <a href="https://3quraniclawslegal.carrd.co">Terms of Service</a>
          {' · '}
          <a href="#">Privacy Policy</a>
          {' · '}
          <a href="#">Contact</a>
          {' · '}
          <a href="https://www.youtube.com/@ReachMohammed/videos">YouTube</a>
        </p>
      </footer>
    </>
  );
}
